import { userIsCustomer } from "~/domains/user/utils/roles";
import { Card } from "~/features/configurator/Linear/components/Card";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { useAddToCartForm } from "~/features/addToCart/hooks/useAddToCartForm";
import { getPackagingLabelAtLineItem } from "~/features/cart/cart/utils/cartUtils";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { Product } from "~/domains/product/Product";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { makeSalesAwarePackCodeProvider } from "~/features/salesCampaign/utils/salesAwarePackCodeProvider";
import { Alert } from "~/common/mui/Alert/Alert";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { FormHelperText, InputLabel, Stack, styled } from "~/common/mui";
import { PriceElements, type PriceProps } from "~/domains/pricing/components/Price/PriceV4";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { calculateSalesCampaign4PDP, useSelectPricingDetails } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { addToCart } from "~/features/addToCart/redux/addToCart/productAddFlow";
import { addToCartItemFromSubmitData } from "~/features/addToCart/redux/addToCart/addToCartItemFactory";
import { SaleDiscountIndicator } from "~/domains/salesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import { renderButtons } from "~/features/saleRecommendations/hooks/useRecommendationTileButtons";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

export interface ProductEntryProps {
  productCode: string;
  reco_type: PurchaseType;
  reco_place: PurchasePlace;
}

export const ProductEntry = ({ productCode, reco_type, reco_place }: ProductEntryProps) => {
  const page = usePurchasePlace();
  const { t } = useTranslation();

  const isTopSeller = reco_type === "top";
  const pnaCallEnabled = !window.SCHA.feature.recoPrice;

  const { form, productDescriptor, main, prepareSubmitData } = useAddToCartForm({
    productCode,
    initialPackCodeProvider: makeSalesAwarePackCodeProvider(null, false),
    fetchAvailability: !isTopSeller,
    fetchPrice: pnaCallEnabled,
  });
  const [addedQuantity, setAddedQuantity] = useState(0);

  const selectedPack = main.selectedPack;
  const packCode = form.watch("selectedPackCode");
  const packSize = main.selectedPackaging?.packSize ?? selectedPack?.packSize ?? 1;

  const quantity = form.watch("quantity");
  const priceReference = form.watch("priceReference");

  const salesHelper = useSalesHelper(productDescriptor);

  const isOnSale = salesHelper.isPackageOnSale(packCode);

  const { data } = useMemo(() => calculateSalesCampaign4PDP({ productDescriptor }), [productDescriptor]);
  const { price, oldPrice, discountFormatted } = useSelectPricingDetails({
    packCode,
    quantity,
    priceReference,
    productCode,
    salesData: data?.salePerPack?.[packCode],
    saleActivated: isOnSale,
  });

  let invalidQuantity = false;
  if (selectedPack) {
    invalidQuantity = quantity === 0 || quantity % packSize > 0;
  }

  const addToCartAction = () => {
    if (quantity) {
      const submitData = prepareSubmitData();
      addToCart({
        items: [
          addToCartItemFromSubmitData({
            submitData,
            productCode: productDescriptor.base.code,
            restrictionQuery: "",
            context: { page, recommendationPlace: reco_place, recommendationType: reco_type },
          }),
        ],
        page,
        source: "unknown",
        successUi: "none",
        recommendationPlace: reco_place,
        recommendationType: reco_type,
      })
        .then(() => {
          setAddedQuantity((prev) => prev + quantity);
        })
        .catch(() => undefined);
    }
  };

  let helperLabel = null;
  if (userIsCustomer()) {
    helperLabel = (
      <span>
        {getPackagingLabelAtLineItem({
          t,
          productDescriptor,
          packSize,
          packCode,
          quantity,
        })}
      </span>
    );
  }

  const canSeePrice = userHasPermission(Permission.PRICE);

  const buttonElement = renderButtons(productDescriptor, {
    atc: {
      onClick: addToCartAction,
      disabled: invalidQuantity,
      // @ts-expect-error tracking attribute
      "tracking-aux-action": "add-to-cart",
      sx: { whiteSpace: "nowrap" },
    },
  })[0];

  return (
    <ProductDescriptorContext value={productDescriptor}>
      <ProductCard
        tracking-aux-prod-name={productDescriptor.base.designation}
        tracking-aux-prod-id={productDescriptor.base.sapMaterialNumber}
      >
        {isOnSale && <PositionedSaleTag size={24} />}
        <Product.PdpLink reco_type={reco_type}>
          <ProductImage clickable={false} />
        </Product.PdpLink>

        <Stack gap={1} sx={{ gridColumnEnd: { "desktop-l": "span 2" } }}>
          <Stack gap={0.5}>
            <DesignationContainer gap={0} direction="row">
              <Product.PreferredIndicator />
              <Product.PdpLink reco_type={reco_type}>
                <Product.DesignationV2 component="p" sx={{ fontWeight: "bold" }} variant={"titleMedium"} />
              </Product.PdpLink>
            </DesignationContainer>

            <Product.PdpLink reco_type={reco_type}>
              <Product.ShortDescription clickable={false} />
            </Product.PdpLink>
          </Stack>

          {window.SCHA.feature.recoAvailability && <Product.StockIndicator />}

          {canSeePrice && pnaCallEnabled && (
            <PriceElements.Stack>
              {productDescriptor.isLoading && <CircleSpinner />}
              {!productDescriptor.isLoading && (
                <>
                  <Stack direction="row" gap={1}>
                    <CurrentPrice price={price} zeroText={t("product.priceOnRequest")} variant="bodyLarge" />
                    <SaleDiscountIndicator.V2 discountFormatted={discountFormatted} />
                  </Stack>
                  {oldPrice && <OldPrice price={oldPrice} variant="bodySmall" />}
                </>
              )}
            </PriceElements.Stack>
          )}
        </Stack>

        <QuantityInputContainer>
          <QuantityInput
            min={packSize}
            value={quantity}
            onValueChange={(e) => form.setValue("quantity", e)}
            variant="compact"
            step={packSize}
            persistentMinValue
            error={invalidQuantity}
            helperLabel={helperLabel}
            disabled={productDescriptor.isLoading && userIsCustomer()}
          />
        </QuantityInputContainer>

        <AddToCartContainer>
          <ButtonLabel shrink={true}>&nbsp;</ButtonLabel>
          {buttonElement}
          {addedQuantity > 0 && (
            <FormHelperText>
              <Stack direction={"row"} gap={0.5} sx={{ alignItems: "center" }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 10 }} />
                {t("addToCart.recommendedProductsAdded", { count: addedQuantity ?? 0 })}
              </Stack>
            </FormHelperText>
          )}
        </AddToCartContainer>

        {invalidQuantity && (
          <QuantityError severity="error">
            {`${t("newContract.quantityErrorTitle")}. ${t("newContract.quantityErrorMessage")}`}
          </QuantityError>
        )}
      </ProductCard>
    </ProductDescriptorContext>
  );
};

const ProductCard = styled(Card)(({ theme }) => ({
  "&&": {
    display: "grid",
    gridTemplateColumns: `${theme.spacing(8)} minmax(0, 1fr)`,
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  "& .MuiFormHelperText-root.MuiFormHelperText-root": {
    marginInline: 0,
  },
  [theme.breakpoints.between("tablet", "desktop-s")]: {
    "&&": {
      gridTemplateColumns: `${theme.spacing(8)} minmax(0, 1fr) ${theme.spacing(21.5)} min-content`,
    },
  },
  [theme.breakpoints.up("desktop-l")]: {
    "&&": {
      gridTemplateColumns: `${theme.spacing(8)} ${theme.spacing(21.5)} minmax(0, 1fr)`,
    },
  },
}));

const QuantityInputContainer = styled("div")(({ theme }) => ({
  width: "100%",
  gridColumn: 2,
  marginTop: theme.spacing(-1),
  [theme.breakpoints.up("tablet")]: {
    gridColumn: "unset",
    marginTop: "unset",
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: 2,
    maxWidth: theme.spacing(21.5),
  },
}));

const PositionedSaleTag = styled(SaleTag)({
  position: "absolute",
  top: 0,
  left: 0,
});

const ProductImage = styled(Product.PrimaryImage)(({ theme }) => ({
  width: theme.spacing(8),
  placeSelf: "center",
  gridRow: 1,
  gridColumn: 1,
}));

const DesignationContainer = styled(Stack)({
  alignItems: "center",
});

const AddToCartContainer = styled("div")(({ theme }) => ({
  gridColumn: 2,
  width: "100%",
  flex: 1,
  [theme.breakpoints.between("tablet", "desktop-s")]: {
    gridColumn: "unset",
  },
  [theme.breakpoints.up("desktop-l")]: {
    gridColumn: 3,
  },
}));

const ButtonLabel = styled(InputLabel)(({ theme }) => ({
  "&&": {
    display: "none",
  },
  [theme.breakpoints.between("tablet", "desktop-s")]: {
    "&&": {
      display: "block",
    },
  },
  [theme.breakpoints.up("desktop-l")]: {
    "&&": {
      display: "block",
    },
  },
}));

const QuantityError = styled(Alert)({
  gridColumn: "1 / -1",
});

const CurrentPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  ...getFontStyle(FontSize.BodySmall),
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));
